import { SchemaSpec } from 'prosemirror-model';
import { EditorContentConfiguration } from '../configurations/editor-content';

function composeNodeStyleString(node): string {
    let strStyle = '';
    if (!!node.attrs.direction) {
        strStyle = strStyle + 'direction:' + node.attrs.direction + ';';
    }
    if (!!node.attrs.textAlign) {
        strStyle = strStyle + 'text-align:' + node.attrs.textAlign + ';';
    }
    if (!!node.attrs.fontSize) {
        strStyle = strStyle + 'font-size:' + node.attrs.fontSize.toLowerCase() + ';';
    }
    if (!!node.attrs.fontFamily) {
        strStyle = strStyle + 'font-family:' + node.attrs.fontFamily.toLowerCase() + ';';
    }
    if (!!node.attrs.lineHeight) {
        strStyle = strStyle + 'line-height:' + node.attrs.lineHeight + ';';
    }
    return strStyle;
}

export class TextEditorSchemaSpecification implements SchemaSpec {
    private defaultLineHeight = 1;

    private defaultFontSize = '14px';
    private defaultTextAlign = 'left';
    private defaultFontFamily = 'arial';
    private defaultDirection = 'ltr';

    private _nodes = null;
    private _marks = null;

    constructor(contentConfiguration: EditorContentConfiguration) {
        if (contentConfiguration.documentFontSize) {
            this.defaultFontSize = contentConfiguration.documentFontSize;
        }
        if (contentConfiguration.paragraphAlign) {
            this.defaultTextAlign = contentConfiguration.paragraphAlign;
        }
        if (contentConfiguration.documentFontFamily) {
            this.defaultFontFamily = contentConfiguration.documentFontFamily;
        }
        if (contentConfiguration.documentTextDirection) {
            this.defaultDirection = contentConfiguration.documentTextDirection;
        }
    }


    get nodes() {
        if (!this._nodes) {
            this._nodes = this.getNodesSpec();
        }
        return this._nodes;
    }

    get marks() {
        if (!this._marks) {
            this._marks = this.getMarksSpec();
        }
        return this._marks;
    }

    private getNodesSpec() {
        return {
            doc: {
                content: 'main+'
            },
            main: {
                content: 'block+',
                parseDOM: [{ tag: 'div' }],
                toDOM() { return ['div', 0]; },
                attrs: {}
            },
            paragraph: {
                content: 'inline*',
                group: 'block',
                attrs: {
                    direction: { default: this.defaultDirection },
                    fontSize: { default: this.defaultFontSize },
                    fontFamily: { default: this.defaultFontFamily },
                    lineHeight: { default: this.defaultLineHeight },
                    textAlign: { default: this.defaultTextAlign },
                },
                toDOM(node) {
                    const strStyle: string = composeNodeStyleString(node);
                    if (!strStyle || (strStyle === '')) {
                        return ['p', 0];
                    }
                    return ['p', { style: strStyle }, 0];
                },
                parseDOM: [
                    {
                        tag: 'p',
                        getAttrs(dom: HTMLElement) {
                            const style: CSSStyleDeclaration = dom.style;
                            return {
                                direction: style.direction,
                                fontSize: style.fontSize.toLowerCase() || this.defaultFontSize,
                                fontFamily: style.fontFamily.replace(/\"/g, '').toLowerCase() || this.defaultFontFamily,
                                textAlign: style.textAlign || this.defaultTextAlign
                            };
                        }
                    }
                ]
            },
            text: {
                inline: true,
                group: 'inline'
            },
            hard_break: {
                inline: true,
                group: 'inline',
                selectable: false, 
                parseDOM: [
                    { tag: 'br' }
                ], 
                toDOM: function toDOM() { 
                    return ['br']; 
                } 
            }

        };
    }

    private getMarksSpec() {
        return {
            italic: {
                parseDOM: [{ tag: 'i' }, { tag: 'em' }, { style: 'font-style=italic' }],
                toDOM() { return ['span', { style: 'font-style:italic' }, 0]; }
            },
            bold: {
                parseDOM: [{ tag: 'strong' },
                { tag: 'b', getAttrs: node => node.style.fontWeight !== 'normal' && null },
                { style: 'font-weight', getAttrs: value => /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null }],
                toDOM() { return ['span', { style: 'font-weight:bold' }, 0]; }
            },
            underline: {
                parseDOM: [{ tag: 'u' }, { style: 'text-decoration=underline' }],
                toDOM() { return ['span', { style: 'text-decoration:underline' }, 0]; }
            }
        };
    }

}
